import React, { useState, useEffect } from "react";
import { format, parseISO, differenceInMinutes, differenceInHours } from "date-fns";
import FlightLocationDisplay from "./FlightLocationDisplay";
import FlightTerminalInfo from "./FlightTerminalInfo";
import FlightHeader from "./FlightHeader";
import { useCheckinUrls } from "@/hooks/useCheckinUrls";
import { FlightDetails } from "@/types/trip";
import Hey from "@/lib/hey-utils";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Star } from "lucide-react";
import { getFlightStatus, FlightStatus as ApiFlightStatus, getFlightStatusLabel } from "@/utils/flightStatus";
import { SeatBadge } from "@/components/ui/seat-badge";

interface FlightSegmentProps {
  flightDetails: FlightDetails;
  departureAirport: Hey.AirportInformations;
  arrivalAirport: Hey.AirportInformations;
  isWithin72Hours?: boolean;
  isWithin48Hours?: boolean;
  handleCheckFlightStatus?: (flightNumber: string) => void;
  tripId?: string;
  destinationName?: string;
  showAddToCalendarButton?: boolean;
  showETicketButton?: boolean;
  isDirectFlight?: boolean;
}

const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (isNaN(minutes) || isNaN(hours)) {
    console.error("Invalid duration provided to formatDuration:", minutes);
    return "-";
  }
  if (hours === 0) {
    return `${remainingMinutes}m`;
  } else if (remainingMinutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${remainingMinutes}m`;
  }
};

const getCabinClassBadgeStyle = (cabinClass?: string) => {
  switch (cabinClass?.toUpperCase()) {
    case 'FIRST':
      return "bg-amber-100 text-amber-800 hover:bg-amber-200";
    case 'BUSINESS':
      return "bg-blue-100 text-blue-800 hover:bg-blue-200";
    case 'PREMIUM_ECONOMY':
      return "bg-purple-100 text-purple-800 hover:bg-purple-200";
    case 'ECONOMY':
    default:
      return "bg-green-100 text-green-800 hover:bg-green-200";
  }
};

const getCabinClassDisplay = (cabinClass?: string, t?: any) => {
  if (!cabinClass) return t("tripCard.economy", "Economy");
  switch (cabinClass.toUpperCase()) {
    case 'FIRST':
      return t("tripCard.first", "First");
    case 'BUSINESS':
      return t("tripCard.business", "Business");
    case 'PREMIUM_ECONOMY':
      return t("tripCard.premiumEconomy", "Premium Economy");
    case 'ECONOMY':
      return t("tripCard.economy", "Economy");
    default:
      return cabinClass;
  }
};

const FlightSegment: React.FC<FlightSegmentProps> = ({
  flightDetails,
  departureAirport,
  arrivalAirport,
  isWithin72Hours = false,
  isWithin48Hours = false,
  handleCheckFlightStatus,
  tripId,
  destinationName = "",
  showAddToCalendarButton = false,
  showETicketButton = true,
  isDirectFlight = true
}) => {
  const {
    checkinUrls,
    getCheckinUrlForFlight
  } = useCheckinUrls(tripId);
  const [showAddToCalendar] = useState(true);
  const {
    t
  } = useTranslation();
  const now = new Date();
  const [flightStatus, setFlightStatus] = useState<ApiFlightStatus | null>(null);
  const [loading, setLoading] = useState(false);

  const handleAddToCalendar = (e: React.MouseEvent) => {
    e.stopPropagation();
    console.debug("Adding flight to calendar:", flightDetails.flightNumber);
    // Calendar adding logic would go here
  };

  useEffect(() => {
    setFlightStatus(flightDetails.flightStatus)
  }, [flightDetails.flightStatus]);

  const flightDurationMinutes = differenceInMinutes(flightDetails.arrival.date, flightDetails.departure.date);
  const flightDuration = formatDuration(flightDurationMinutes);

  const departureTime = parseISO(flightDetails.departure.time || "1970");
  const calculatedIsWithin72Hours = isWithin72Hours || differenceInHours(departureTime, now) <= 72;
  const calculatedIsWithin48Hours = isWithin48Hours || differenceInHours(departureTime, now) <= 48;

  const departureStatus = flightStatus && flightStatus.latestStatus && flightStatus.latestStatus.estimatedDepartureLocalDate ? getFlightStatusLabel(flightStatus.scheduledDepartureLocalDate, flightStatus.latestStatus.estimatedDepartureLocalDate) : null;
  const arrivalStatus = flightStatus && flightStatus.latestStatus && flightStatus.latestStatus.estimatedArrivalLocalDate ? getFlightStatusLabel(flightStatus.scheduledArrivalLocalDate, flightStatus.latestStatus.estimatedArrivalLocalDate) : null;
  const isFlightCanceled = flightStatus && flightStatus.latestStatus && flightStatus.latestStatus.flightStatus?.toLowerCase().includes('cancel') || false;

  const getStatusText = () => {
    if (isFlightCanceled) return 'CANCELED';
    if (departureStatus && Math.abs(departureStatus.diffMinutes) > 5) {
      if (departureStatus.diffMinutes > 0) return 'DELAYED';
      if (departureStatus.diffMinutes < 0) return 'EARLY';
    }
    if (arrivalStatus && Math.abs(arrivalStatus.diffMinutes) > 5) {
      if (arrivalStatus.diffMinutes > 0) return 'DELAYED';
      if (arrivalStatus.diffMinutes < 0) return 'EARLY';
    }
    if (flightStatus?.latestStatus?.flightStatus) {
      return 'ON TIME';
    }
    return null;
  };

  const statusText = getStatusText();

  const getStatusTextColor = () => {
    if (statusText === 'CANCELED') return 'text-red-600';
    if (statusText === 'DELAYED') return 'text-orange-600';
    if (statusText === 'EARLY') return 'text-green-600';
    return 'text-gray-600';
  };

  const estimatedDepartureTime = flightStatus?.latestStatus?.estimatedDepartureLocalDate ? format(parseISO(flightStatus.latestStatus.estimatedDepartureLocalDate || "1970"), 'HH:mm') : null;
  const estimatedArrivalTime = flightStatus?.latestStatus?.estimatedArrivalLocalDate ? format(parseISO(flightStatus.latestStatus.estimatedArrivalLocalDate || "1970"), 'HH:mm') : null;
  const actualDepartureTime = flightStatus?.latestStatus?.actualDepartureLocalDate ? format(parseISO(flightStatus.latestStatus.actualDepartureLocalDate || "1970"), 'HH:mm') : null;
  const actualArrivalTime = flightStatus?.latestStatus?.actualArrivalLocalDate ? format(parseISO(flightStatus.latestStatus.actualArrivalLocalDate || "1970"), 'HH:mm') : null;

  const modifiedFlightDetailsDeparture = {
    ...flightDetails,
    departure: {
      ...flightDetails.departure,
      time: flightStatus?.latestStatus?.actualDepartureLocalDate || flightDetails.departure.time
    }
  };

  const modifiedFlightDetailsArrival = {
    ...flightDetails,
    arrival: {
      ...flightDetails.arrival,
      time: flightStatus?.latestStatus?.actualArrivalLocalDate || flightDetails.arrival.time
    }
  };

  const departureTimeDiff = flightStatus?.latestStatus?.actualDepartureLocalDate && flightStatus?.latestStatus?.estimatedDepartureLocalDate ? differenceInMinutes(parseISO(flightStatus.latestStatus.actualDepartureLocalDate || "1970"), parseISO(flightStatus.latestStatus.estimatedDepartureLocalDate || "1970")) : 0;
  const arrivalTimeDiff = flightStatus?.latestStatus?.actualArrivalLocalDate && flightStatus?.latestStatus?.estimatedArrivalLocalDate ? differenceInMinutes(parseISO(flightStatus.latestStatus.actualArrivalLocalDate || "1970"), parseISO(flightStatus.latestStatus.estimatedArrivalLocalDate || "1970")) : 0;

  const showDepartureActualVsEstimated = !!actualDepartureTime && !!estimatedDepartureTime && Math.abs(departureTimeDiff) > 5 && actualDepartureTime !== estimatedDepartureTime;
  const showArrivalActualVsEstimated = !!actualArrivalTime && !!estimatedArrivalTime && Math.abs(arrivalTimeDiff) > 5 && actualArrivalTime !== estimatedArrivalTime;

  const hasDepartureTimeDifference = departureStatus && Math.abs(departureStatus.diffMinutes) > 0;
  const hasArrivalTimeDifference = arrivalStatus && Math.abs(arrivalStatus.diffMinutes) > 0;

  const getDepartureTimeDifference = () => {
    if (isFlightCanceled) return null;
    if (!departureStatus || Math.abs(departureStatus.diffMinutes) <= 5) return null;
    const minutes = Math.abs(departureStatus.diffMinutes);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours > 0) {
      return `${departureStatus.diffMinutes > 0 ? '+' : '-'}${hours}h${remainingMinutes > 0 ? ` ${remainingMinutes}m` : ''}`;
    } else {
      return `${departureStatus.diffMinutes > 0 ? '+' : '-'}${minutes}m`;
    }
  };

  const getArrivalTimeDifference = () => {
    if (isFlightCanceled) return null;
    if (!arrivalStatus || Math.abs(arrivalStatus.diffMinutes) <= 5) return null;
    const minutes = Math.abs(arrivalStatus.diffMinutes);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours > 0) {
      return `${arrivalStatus.diffMinutes > 0 ? '+' : '-'}${hours}h${remainingMinutes > 0 ? ` ${remainingMinutes}m` : ''}`;
    } else {
      return `${arrivalStatus.diffMinutes > 0 ? '+' : '-'}${minutes}m`;
    }
  };

  const getStatusColor = () => {
    if (!flightStatus || !flightStatus.latestStatus) return '';
    if (isFlightCanceled) return 'text-red-500';
    if (!departureStatus) return '';
    if (departureStatus.diffMinutes > 0) return 'text-orange-500';
    if (departureStatus.diffMinutes < 0) return 'text-green-500';
    return 'text-gray-500';
  };

  const isDirect = isDirectFlight;

  return (
    <div className="border border-travel-100 rounded-xl overflow-hidden bg-white">
      <div className="p-3">
        <FlightHeader 
          flightDetails={flightDetails} 
          isWithin72Hours={calculatedIsWithin72Hours} 
          isWithin48Hours={calculatedIsWithin48Hours} 
          checkinUrl={getCheckinUrlForFlight(flightDetails.flightNumber, flightDetails.departure.time)} 
          onAddToCalendar={showAddToCalendar ? handleAddToCalendar : undefined} 
          tripId={tripId} 
          destinationName={destinationName} 
          showAddToCalendarButton={showAddToCalendarButton} 
        />
        
        <div className="mt-2 flex justify-between items-center">
          {flightDetails.cabinClass && (
            <Badge 
              variant="outline" 
              className={`text-xs flex items-center gap-1 ${getCabinClassBadgeStyle(flightDetails.cabinClass)}`}
            >
              <Star className="w-3 h-3" />
              <span>{getCabinClassDisplay(flightDetails.cabinClass, t)}</span>
            </Badge>
          )}
          
          {flightDetails.seat && (
            <SeatBadge 
              variant={flightDetails.cabinClass?.toUpperCase() === 'BUSINESS' ? 'business' : 
                      flightDetails.cabinClass?.toUpperCase() === 'FIRST' ? 'secondary' : 'economy'}
              seatNumber={flightDetails.seat}
              className="ml-2"
            />
          )}
          
          {statusText && (
            <span className={`${getStatusTextColor()} text-xs font-bold ml-auto`}>
              {statusText}
            </span>
          )}
        </div>
      </div>

      <div className="flex justify-between items-start p-4 bg-gray-50">
        <FlightLocationDisplay 
          locationCode={flightDetails.departure.code} 
          locationName={departureAirport.cityName || ""} 
          time={actualDepartureTime && estimatedDepartureTime && actualDepartureTime === estimatedDepartureTime ? modifiedFlightDetailsDeparture.departure.time : flightDetails.departure.time} 
          timezone={flightDetails.departure.timezone}
          terminal={flightStatus?.latestStatus?.departureTerminal || flightDetails.departure.terminal} 
          gate={flightStatus?.latestStatus?.departureGate || flightDetails.departure.gate} 
          isArrival={false} 
          estimatedTime={estimatedDepartureTime} 
          secondaryTime={showDepartureActualVsEstimated ? actualDepartureTime : null} 
          hasTimeDifference={!!estimatedDepartureTime && estimatedDepartureTime !== format(parseISO(flightDetails.departure.time || "1970"), 'HH:mm')} 
          diffMinutes={departureStatus?.diffMinutes || 0} 
          isCanceled={isFlightCanceled} 
          airportName={flightDetails.departure.airportName || departureAirport.airportName} 
          isFullScreen={tripId?.includes('fullscreen')}
        />

        <div className="flex flex-col items-center">
          <div className="text-xs text-travel-500 mb-1">{formatDuration(flightDurationMinutes)}</div>
          <div className="w-12 border-t border-dashed border-travel-300 my-1"></div>
          {isDirectFlight && <div className="text-xs text-travel-500">{t("tripCard.direct", "Direct")}</div>}
        </div>

        <FlightLocationDisplay 
          locationCode={flightDetails.arrival.code} 
          locationName={arrivalAirport.cityName || ""} 
          time={actualArrivalTime && estimatedArrivalTime && actualArrivalTime === estimatedArrivalTime ? modifiedFlightDetailsArrival.arrival.time : flightDetails.arrival.time} 
          timezone={flightDetails.arrival.timezone}
          terminal={flightStatus?.latestStatus?.arrivalTerminal || flightDetails.arrival.terminal} 
          gate={flightStatus?.latestStatus?.arrivalGate || flightDetails.arrival.gate} 
          isArrival={true} 
          estimatedTime={estimatedArrivalTime} 
          secondaryTime={showArrivalActualVsEstimated ? actualArrivalTime : null} 
          hasTimeDifference={!!estimatedArrivalTime && estimatedArrivalTime !== format(parseISO(flightDetails.arrival.time || "1970"), 'HH:mm')} 
          diffMinutes={arrivalStatus?.diffMinutes || 0} 
          isCanceled={isFlightCanceled} 
          actualArrivalTime={actualArrivalTime} 
          airportName={flightDetails.arrival.airportName || arrivalAirport.airportName} 
          isFullScreen={tripId?.includes('fullscreen')}
        />
      </div>

      <div className="px-4 pb-4 bg-gray-50">
        <FlightTerminalInfo 
          departure={{
            terminal: flightDetails.computed?.departureTerminal,
            gate: flightDetails.computed?.departureGate
          }} 
          arrival={{
            terminal: flightDetails.computed?.arrivalTerminal,
            gate: flightDetails.computed?.arrivalGate
          }} 
        />
      </div>
    </div>
  );
};

export default FlightSegment;
